import { Link } from "react-router-dom";
import "./Footer.css";
import WhatsAppButton from "../components/WhatsappButton";
import React, { useState } from 'react';
import { TransitionProps } from '@mui/material/transitions';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormHelperText, InputLabel, MenuItem, Select, SelectChangeEvent, Slide, Stack, TextField } from "@mui/material";
import CircularProgress from '@mui/material/CircularProgress';
import { fetchAuthSession } from 'aws-amplify/auth';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

type ContactFormValues = {
	nombre: string;
	apellido: string;
	email: string;
	organizacion: string;
	tipoOrganizacion: string;
	mensaje: string;
};

type ContactFormErrors = Partial<Record<keyof ContactFormValues, string>>;

const emptyContactForm: ContactFormValues = {
	nombre: "",
	apellido: "",
	email: "",
	organizacion: "",
	tipoOrganizacion: "",
	mensaje: "",
};

const Transition = React.forwardRef(function Transition(
	props: TransitionProps & {
		children: React.ReactElement<any, any>;
	},
	ref: React.Ref<unknown>,
) {
	return <Slide direction="up" ref={ref} {...props} />;
});

export const Footer = () => {
	const fieldSpacing = { my: 0 };

	const scrollToTop = () => {
		window.scrollTo(0, 0);
	};

	const [open, setOpen] = useState(false);
	const [submiting, setSubmiting] = useState(false);
	const [contactForm, setContactForm] = useState<ContactFormValues>(emptyContactForm);
	const [contactErrors, setContactErrors] = useState<ContactFormErrors>({});
	const [submitError, setSubmitError] = useState("");
	const [submitSuccess, setSubmitSuccess] = useState("");

	const handleCloseModal = () => {
		setOpen(false);
	};
	const handleOpenModal = () => {
		setOpen(true);
	};

	const handleFieldChange = (field: keyof ContactFormValues) => (
		event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
	) => {
		setContactForm((currentForm) => ({
			...currentForm,
			[field]: event.target.value,
		}));
		setContactErrors((currentErrors) => ({
			...currentErrors,
			[field]: undefined,
		}));
		setSubmitError("");
		setSubmitSuccess("");
	};

	const handleOrganizationChange = (event: SelectChangeEvent<string>) => {
		setContactForm((currentForm) => ({
			...currentForm,
			tipoOrganizacion: event.target.value,
		}));
		setContactErrors((currentErrors) => ({
			...currentErrors,
			tipoOrganizacion: undefined,
		}));
		setSubmitError("");
		setSubmitSuccess("");
	};

	const validateContactForm = () => {
		const nextErrors: ContactFormErrors = {};
		const maxLengthFields: Array<keyof Omit<ContactFormValues, "mensaje">> = [
			"nombre",
			"apellido",
			"email",
			"organizacion",
			"tipoOrganizacion",
		];

		if (!contactForm.nombre.trim()) nextErrors.nombre = "Ingresa tu nombre";
		if (!contactForm.apellido.trim()) nextErrors.apellido = "Ingresa tu apellido";
		if (!contactForm.email.trim()) nextErrors.email = "Ingresa tu correo electrónico";
		if (!contactForm.organizacion.trim()) nextErrors.organizacion = "Ingresa tu organización";
		if (!contactForm.tipoOrganizacion.trim()) nextErrors.tipoOrganizacion = "Selecciona un tipo de organización";
		if (!contactForm.mensaje.trim()) nextErrors.mensaje = "Ingresa tu mensaje";

		maxLengthFields.forEach((field) => {
			if (contactForm[field].length > 100) {
				nextErrors[field] = "Máximo 100 caracteres";
			}
		});
		if (contactForm.mensaje.length > 1000) {
			nextErrors.mensaje = "Máximo 1000 caracteres";
		}

		setContactErrors(nextErrors);
		return Object.keys(nextErrors).length === 0;
	};

	const submit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		if (!validateContactForm()) {
			return;
		}

		try {
			setSubmiting(true);
			setSubmitError("");
			setSubmitSuccess("");

			const headers: Record<string, string> = {
				"Content-Type": "application/json",
			};

			try {
				const sesion = await fetchAuthSession();
				const token = sesion.tokens?.idToken;

				if (token) {
					headers.Authorization = `Bearer ${token}`;
				}
			} catch {
				// El formulario puede enviarse de forma anónima.
			}

			const response = await fetch(`${API_BASE_URL}/contact`, {
				method: "POST",
				headers,
				body: JSON.stringify({
					nombre: contactForm.nombre.trim(),
					apellido: contactForm.apellido.trim(),
					email: contactForm.email.trim(),
					organizacion: contactForm.organizacion.trim(),
					tipoOrganizacion: contactForm.tipoOrganizacion,
					mensaje: contactForm.mensaje.trim(),
					url: `${window.location.pathname}${window.location.search}${window.location.hash}`,
				}),
			});

			if (!response.ok) {
				throw new Error("No se pudo enviar el formulario");
			}

			setContactForm(emptyContactForm);
			setContactErrors({});
			setSubmitSuccess("Mensaje enviado correctamente.");
		} catch (error) {
			setSubmitError("No se pudo enviar el mensaje. Inténtalo nuevamente.");
		} finally {
			setSubmiting(false);
		}
	};

	return (
		<footer className="site-footer">
			<section className="site-footer-content">
				<div className="site-footer-anid">
					<a href="https://anid.cl" target="_blank" rel="noreferrer">
						<img src="/landing-assets/anid-white.png" alt="ANID Gobierno de Chile" />
					</a>
				</div>

				<div className="site-footer-developers">
					<p className="site-footer-label">Desarrolla:</p>
					<div className="site-footer-logos" aria-label="Organizaciones desarrolladoras en el pie de página">
						<a href="https://www.hubtec.cl" target="_blank" rel="noreferrer">
							<img className="site-footer-logo-hubtec" src="/images/go/footer_logo_2025.png" alt="Hubtec" />
						</a>
						<a href="https://knowhub.cl/" target="_blank" rel="noreferrer">
							<img className="site-footer-logo-knowhub" src="/images/go/logo_knowhub_blanco.png" alt="KnowHub" />
						</a>
						<a href="https://www.hubapta.com/" target="_blank" rel="noreferrer">
							<img className="site-footer-logo-apta" src="/images/go/logo_APTA_blanco.png" alt="APTA" />
						</a>
					</div>
				</div>

				<div className="site-footer-support">
					<p className="site-footer-help-title">¿Necesitas ayuda?</p>
					
					<button type="button" onClick={handleOpenModal} className="site-footer-support-button">
						<span className="site-footer-support-button-label">Contáctanos</span>
					</button>
					<div className="site-footer-help-copy">y te responderemos a la brevedad.</div>
					<div className="site-footer-links">
						<Link onClick={scrollToTop} to={{ pathname: "/politicas" }} target="_blank">
							Política de Privacidad
						</Link>
						<Link onClick={scrollToTop} to={{ pathname: "/condiciones" }} target="_blank">
							Términos y Condiciones
						</Link>
					</div>
				</div>
			</section>

			<WhatsAppButton onContactClick={handleOpenModal}/>

			<Dialog
					open={open}
					TransitionComponent={Transition}
					keepMounted
					onClose={handleCloseModal}
					aria-labelledby="footer-contact-dialog-title"
					fullWidth
					maxWidth="xs"
				>
					<DialogTitle id="footer-contact-dialog-title">Contáctanos</DialogTitle>
					<DialogContent>
						<form className="site-footer-contact-form" onSubmit={submit}>
							{submitError && <Alert severity="error">{submitError}</Alert>}
							{submitSuccess && <Alert severity="success">{submitSuccess}</Alert>}

							<TextField
								required
								fullWidth
								sx={fieldSpacing}
								id="footer-contact-name"
								type="text"
								label="Nombre"
								value={contactForm.nombre}
								onChange={handleFieldChange("nombre")}
								error={Boolean(contactErrors.nombre)}
								helperText={contactErrors.nombre}
								inputProps={{ maxLength: 100 }}
							/>
							<TextField
								required
								fullWidth
								sx={fieldSpacing}
								id="footer-contact-lastname"
								type="text"
								label="Apellido"
								value={contactForm.apellido}
								onChange={handleFieldChange("apellido")}
								error={Boolean(contactErrors.apellido)}
								helperText={contactErrors.apellido}
								inputProps={{ maxLength: 100 }}
							/>
							<TextField
								required
								fullWidth
								sx={fieldSpacing}
								id="footer-contact-email"
								label="Correo electrónico"
								type="email"
								value={contactForm.email}
								onChange={handleFieldChange("email")}
								error={Boolean(contactErrors.email)}
								helperText={contactErrors.email}
								inputProps={{ maxLength: 100 }}
							/>
							<TextField
								required
								fullWidth
								sx={fieldSpacing}
								id="footer-contact-organization-name"
								type="text"
								label="Organización"
								value={contactForm.organizacion}
								onChange={handleFieldChange("organizacion")}
								error={Boolean(contactErrors.organizacion)}
								helperText={contactErrors.organizacion}
								inputProps={{ maxLength: 100 }}
							/>

							<FormControl required fullWidth error={Boolean(contactErrors.tipoOrganizacion)}>
								<InputLabel id="footer-contact-organization-label">Tipo de organización</InputLabel>
								<Select
									fullWidth
									labelId="footer-contact-organization-label"
									id="footer-contact-organization"
									label="Tipo de organización"
									sx={fieldSpacing}
									value={contactForm.tipoOrganizacion}
									onChange={handleOrganizationChange}
								>
									<MenuItem value={"Institución Educación Superior (IES); Centro I+D / Tecnológico"}>Institución Educación Superior (IES); Centro I+D / Tecnológico</MenuItem>
									<MenuItem value={"Hub/ Incubadora /Aceleradora"}>Hub/ Incubadora /Aceleradora</MenuItem>
									<MenuItem value={"Organismo Sector Público"}>Organismo Sector Público</MenuItem>
									<MenuItem value={"Empresa"}>Empresa</MenuItem>
									<MenuItem value={"StartUp / EBCT"}>StartUp / EBCT</MenuItem>
									<MenuItem value={"Fondo de Inversión"}>Fondo de Inversión</MenuItem>
									<MenuItem value={"Cuenta individual"}>Cuenta individual</MenuItem>
								</Select>
								{contactErrors.tipoOrganizacion && <FormHelperText>{contactErrors.tipoOrganizacion}</FormHelperText>}
							</FormControl>

							<TextField
								fullWidth
								sx={fieldSpacing}
								id="footer-contact-message"
								label="Mensaje"
								required
								type="text"
								multiline
								rows={2}
								value={contactForm.mensaje}
								onChange={handleFieldChange("mensaje")}
								error={Boolean(contactErrors.mensaje)}
								helperText={contactErrors.mensaje}
								inputProps={{ maxLength: 1000 }}
							/>

							<Stack spacing={1} direction="row" justifyContent="center" marginTop={2}>
								<Button variant="contained" className="app-primary-button" type="submit" disabled={submiting}>
									{
										submiting?
										<CircularProgress size={24} color="inherit" />
										:
										'Enviar'
									}
								</Button>
							</Stack>
						</form>
					</DialogContent>
					<DialogActions>
						<Button onClick={handleCloseModal}>Cerrar</Button>
					</DialogActions>
				</Dialog>
		</footer>
	);
};
